// Copyright 2017 Sorama B.V.

import { autoinject, BindingEngine, computedFrom, Disposable, observable, transient } from "aurelia-framework";
import { WorkflowModalCatalog } from "catalogs/WorkflowModalCatalog";
import { Range as PlotRange } from "controls/plot/IPlotParent";
import { Range, Spectrum } from "datatypes/SoramaLocalApi";
import {
    GenericMeasurement,
    MeasurementPartType,
    Point2D,
    Point3D,
    SpectrumSetAnalysisProperties,
} from "datatypes/SoramaWebApi";
import { FileNameConversion } from "extensions/FileNameConversion";
import { MeasurementExtensions } from "extensions/MeasurementExtensions";
import { ObjectExtensions } from "extensions/ObjectExtensions";
import { Chameleon } from "libraries/Chameleon/Chameleon";
import { IExportable } from "libraries/Export/IExportable";
import { OctaveBands } from "libraries/Math/OctaveBands";
import { SVGNode } from "libraries/SVG/SVGNode";
import {
    IWorkflowModuleExportVisual,
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
    IWorkflowModuleModalInstance,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { userSetting } from "libraries/WorkflowFramework/UserSettingsAttribute";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { ImageExportState, LogoPositions } from "modules/modals/ImageExport/ImageExportModal";
import { SettingsModalState } from "modules/modals/SettingsModal";
import { TimeDataSet, TimeSelectionModalState } from "modules/modals/TimeSelection/TimeSelectionModal";
import { WeightedPressureUnitParser } from "parsers/WeightedPressureUnitParser";
import { MeasurementField, MeasurementRepository } from "repositories/MeasurementRepository";
import { IColorMap } from "services/ColorMap/IColorMap";
import { IColorMapService } from "services/ColorMap/IColorMapService";
import { FrequencySelectionService, SelectableRange } from "services/FrequencySelection/FrequencySelectionService";
import { HardwareSampleRate } from "services/Hardware/HardwareSamplerRate";
import { IMeasurementProcessorResolver } from "services/Measurement/IMeasurementProcessorResolver";
import { NotificationUrgency } from "services/Notification/INotificationService";
import { IPlayablePropertiesService } from "services/PlayableProperties/IPlayablePropertiesService";
import { RecommendationEngine } from "services/RecommendationEngine/RecommendationEngine";
import { ISpectralProcessingService } from "services/SoundProcessing/ISpectralProcessingService";
import { Frequency } from "units/Frequency";
import { Pressure } from "units/Pressure";
import { Time } from "units/Time";
import { CriticalBandUnit, Unit, UnitValue, WeightedUnit } from "units/Unit";
import { UnitSpectrum } from "units/UnitSpectrum";
import { WeightedSpectrum } from "units/WeightedSpectrum";
import { NoneWeighting, ZWeighting } from "units/Weighting";
import {
    AllPsychoAcousticMetricTypes,
    PsychoAcousticBandUnits,
    PsychoAcousticMetricType,
    PsychoAcousticUnits,
} from "../PsychoAcoustics/SpiderChart/SpiderChart";
import { TimeDataSetWithAudio } from "../Time/TimeModule";
import { ISpectrogramProvider, UnitSpectrogram } from "./Providers/ISpectrogramProvider";
import { PsychoacousticsSpectrogramProvider } from "./Providers/PsychoacousticsSpectrogramProvider";
import { SpectrogramProvider } from "./Providers/SpectrogramProvider";

@transient()
@autoinject
export class SpectrogramModule implements IWorkflowModuleLifetime, IWorkflowModuleExportVisual {
    private static instanceCount = 0;

    //#region Constants

    @computedFrom("variant")
    public get mininumDisplayedYAxisValue(): UnitValue {
        if (this.variant) {
            return new UnitValue(0.5, PsychoAcousticBandUnits[this.variant]);
        }

        // Results from the lower frequency of the second octave band (44-88 Hz) which is the first band that does not suffer from DC leakage when streaming
        return new UnitValue(OctaveBands.OctaveBandFrequencies[1].lower, Frequency.hertz);
    }

    @computedFrom("variant")
    public get maximumDisplayedYAxisValue() {
        if (this.variant) {
            return new UnitValue(26.5, PsychoAcousticBandUnits[this.variant]);
        }
        // Get the nyquist frequency given the default sample rate
        return new UnitValue(HardwareSampleRate.defaultCAMSampleRate / 2, Frequency.hertz);
    }

    private moduleInstance: IWorkflowModuleInstance;
    private soundProcessingService: ISpectralProcessingService;
    private spectrogramProvider: ISpectrogramProvider;
    private showFrequencySelection = false;
    //#endregion

    //#region  Module properties
    private variant?: PsychoAcousticMetricType;

    @computedFrom("busy", "measurement")
    public get disableExport(): boolean {
        return this.busy || !this.measurement;
    }
    //#endregion

    //#region User Settings Storage Keys
    private static spectrogramColorMapSettingKey = "SpectrogramModuleSpectrogramColorMap";
    //#endregion
    private timeRangeModal: IWorkflowModuleModalInstance<TimeSelectionModalState, void>;
    private spectrogramSettingsModal: IWorkflowModuleModalInstance<SettingsModalState<SpectrogramModule>, void>;
    //#region View Fields
    public progressDiv: HTMLDivElement;

    private dataExportable: IExportable;
    private colorbarExportable: IExportable;

    @computedFrom("variant")
    public get availableUnits(): WeightedUnit[] {
        if (this.variant) {
            return [PsychoAcousticUnits[this.variant] as WeightedUnit];
        }

        return [Pressure.dbSPL, Pressure.dbASPL, Pressure.dbBSPL, Pressure.dbCSPL];
    }

    // The unit of the 2D data, pressure for normal spectrogram. Could also be considered zAxisUnit
    public dataUnit: Unit;

    // Needed to give the color a unit to start with (otherwise it will see that it cannot convert and fail)
    @computedFrom("availableUnits")
    protected get defaultDataUnit(): Unit {
        return this.availableUnits[0];
    }

    @computedFrom("variant")
    public get displayYAxisUnit(): Unit {
        if (this.variant) {
            return PsychoAcousticBandUnits[this.variant];
        }
        return Frequency.hertz;
    }
    public displayTimeUnit: Unit = Time.second;
    public displayTimeSelectionUnit: Unit = Time.milliSecond;

    public recommendedFreqRange: PlotRange[] = [];

    protected criticalBandRanges: SelectableRange[] = [];

    public zoomModes: { name: string; value: string }[] = [
        { name: "X Axis", value: "x" },
        { name: "Y Axis", value: "y" },
        { name: "XY Axis", value: "xy" },
    ];

    @userSetting()
    public zoomMode = "xy";

    public spectra: WeightedSpectrum[];

    public progress = 0;

    /**
     * Contains a "cached" list of time signals
     */
    private currentTimeSignals: TimeDataSet[] = [];

    public localPlayableId: string;
    public globalPlayableId: string = IPlayablePropertiesService.GlobalTimePlayablePropertyId;
    public playableId = IPlayablePropertiesService.GlobalTimePlayablePropertyId;

    private currentDataId: string;
    //#endregion

    //#region State Fields
    private busy: boolean;

    private measurementId: string;
    private measurement: GenericMeasurement;

    private _spectrumColorMap: IColorMap;

    private disposables: Disposable[] = [];
    private measurementUpdateDisposables: Disposable[] = [];

    //#endregion

    //#region Workflow Properties
    private oldSelectedFrequencies: SelectableRange[];

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, objectType: "selectableRange" })
    public frequencyRanges: SelectableRange[] = [];

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, objectType: "range" })
    public timeSelectionRanges: SelectableRange[] = [];

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public isFrequencyRangeLinked: boolean;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public isTimeRangeLinked: boolean;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public frequencyRange: Range;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public timeRange: Range;

    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        objectType: "string",
    })
    @observable
    public selectedMeasurementIds: string[];

    @workflowProperty({
        bindingMode: WorkflowPropertyBindingMode.twoWay,
        objectType: "string",
        changedHandler: "selectedMeasurementIdsChanged",
    })
    public selectedPsychoAcousticMeasurementIds: string[] = [];

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay, objectType: "Point3D" })
    public selectedMic: Point3D;

    //#endregion

    //#region Settings
    public minXAxisBounds: number;
    public maxXAxisBounds: number;

    public minYAxisBounds: number;
    public maxYAxisBounds: number;

    @observable({ changeHandler: "frequencyRangeInputChanged" })
    public minYAxis: number;

    @observable({ changeHandler: "frequencyRangeInputChanged" })
    public maxYAxis: number;

    @observable({ changeHandler: "timeRangeInputChanged" })
    public minXAxis: number;

    @observable({ changeHandler: "timeRangeInputChanged" })
    public maxXAxis: number;

    @computedFrom("_spectrumColorMap")
    public get colorMap(): IColorMap {
        return this._spectrumColorMap;
    }
    public set colorMap(value: IColorMap) {
        this._spectrumColorMap = value;
        this.colorMapService.setDefaultColorMap(SpectrogramModule.spectrogramColorMapSettingKey, value);
    }

    //#endregion

    //#region Settings

    @observable
    @userSetting({ parser: new WeightedPressureUnitParser() })
    public selectedWeightedDataUnit: WeightedUnit;

    //#endregion

    //#region Constructor and Initialisation
    constructor(
        private readonly measurementRepository: MeasurementRepository,
        private readonly processorResolver: IMeasurementProcessorResolver,
        private readonly colorMapService: IColorMapService,
        private readonly bindingEngine: BindingEngine,
        private readonly playablePropertiesService: IPlayablePropertiesService,
        private readonly frequencySelectionService: FrequencySelectionService
    ) {
        this.updateData = this.updateData.bind(this); // Bind this to the updateData function so it can be used as a callback
    }

    public bind(): void {
        this.disposables.push(
            this.frequencySelectionService.subscribeToRangeChanges((rangePropertiesChanged) => {
                if (!rangePropertiesChanged) {
                    this.selectedFrequenciesChanged(this.frequencySelectionService.ranges);
                }
            })
        );
    }

    public activateModule(moduleInstance: IWorkflowModuleInstance): void {
        this.moduleInstance = moduleInstance;
        if (this.moduleInstance.variant) {
            if (AllPsychoAcousticMetricTypes.includes(this.moduleInstance.variant as PsychoAcousticMetricType)) {
                this.variant = this.moduleInstance.variant as PsychoAcousticMetricType;
            } else {
                console.warn(
                    "Invalid variant for Spectrogram Module: " +
                        this.moduleInstance.variant +
                        ". It is not a PsychoAcoustic Metric Type."
                );
            }
        }

        if (this.variant) {
            this.moduleInstance.title = `${this.variant} - Spectrogram`;
            this.spectrogramProvider = new PsychoacousticsSpectrogramProvider(this.processorResolver, this.variant);
            this.isFrequencyRangeLinked = false;
        } else {
            this.spectrogramProvider = new SpectrogramProvider(this.processorResolver);
        }

        this.localPlayableId = SpectrogramModule.name + SpectrogramModule.instanceCount++;
        this.selectedWeightedDataUnit = this.availableUnits[0];
        this.disposables = [];

        this.minYAxis = this.mininumDisplayedYAxisValue.value;
        this.maxYAxis = this.maximumDisplayedYAxisValue.value;

        const promises = [
            this.colorMapService
                .getDefaultColorMap(SpectrogramModule.spectrogramColorMapSettingKey)
                .then((colorMap) => {
                    this.colorMap = colorMap;
                }),
        ];

        Promise.all(promises).then(() => {
            const initialFrequencyRanges =
                this.frequencyRanges && this.frequencyRanges.length > 0
                    ? this.frequencyRanges
                    : this.frequencySelectionService.ranges;
            this.selectedFrequenciesChanged(initialFrequencyRanges);
            this.updateData();
        });

        this.selectedMeasurementIdsChanged();
    }

    public deactivateModule(): void {
        if (this.playablePropertiesService.playableProperties[this.localPlayableId]) {
            this.playablePropertiesService.unregister(this.localPlayableId);
        }

        while (this.measurementUpdateDisposables.length > 0) {
            this.measurementUpdateDisposables.pop().dispose();
        }

        while (this.disposables.length > 0) {
            this.disposables.pop().dispose();
        }
    }

    //#endregion

    //#region Change callbacks
    // Copied function from SpectrumModule to enable showing frequency ranges
    public selectedFrequenciesChanged(
        newRanges: SelectableRange[],
        old?: SelectableRange[],
        propertyName?: string,
        force?: boolean
    ): void {
        this.frequencyRanges.splice(0, this.frequencyRanges.length, ...newRanges);

        if (!ObjectExtensions.deepEqual(this.frequencySelectionService.ranges, newRanges)) {
            this.frequencySelectionService.ranges.splice(0, this.frequencySelectionService.ranges.length, ...newRanges);
        }

        const oldValue = this.oldSelectedFrequencies;
        this.oldSelectedFrequencies = newRanges ? [...newRanges] : undefined;

        if (!oldValue) {
            // The first change is always triggered by the binding engine, ignore this change to prevent the selected frequency being unset
            return;
        }

        if (this.frequencySelectionService.ranges.length > 0) {
            Chameleon.setObservable("spectrogram", true);
        } else {
            Chameleon.setObservable("spectrogram", false);
        }
        Chameleon.checkObservable("spectrogram");

        this.activeRangesChanged();
    }

    // Called when the user selects a range on the plot's frequency/band axis
    protected selectionUpdated(selection: SelectableRange[]): void {
        // No need to do anything on the selection for a normal spectrum
        if (!this.variant) return;

        // Replace frequency selection ranges with critical band ranges converted to hertz
        const newFrequencyRangesHz = this.criticalBandRanges.map((range) => {
            const fromHz = (this.displayYAxisUnit as CriticalBandUnit).convertBandToHz(range.from - 0.5);
            const toHz = (this.displayYAxisUnit as CriticalBandUnit).convertBandToHz(range.to + 0.5);
            const hzRange = new SelectableRange(fromHz, toHz);
            hzRange.isSelected = range.isSelected;
            return hzRange;
        });
        this.frequencySelectionService.ranges.splice(
            0,
            this.frequencySelectionService.ranges.length,
            ...newFrequencyRangesHz
        );
    }

    private frequencyRangeChanged(newRange: Range): void {
        if (this.isFrequencyRangeLinked && newRange) {
            this.minYAxis = newRange.from;
            this.maxYAxis = newRange.to;
        }
    }

    private timeRangeChanged(newRange: Range): void {
        if (this.isTimeRangeLinked && newRange) {
            this.minXAxis = newRange.from;
            this.maxXAxis = newRange.to;
        }
    }

    private frequencyRangeInputChanged(): void {
        this.frequencyRange = <Range>{
            from: this.minYAxis,
            to: this.maxYAxis,
        };
    }

    private timeRangeInputChanged(): void {
        this.timeRange = <Range>{
            from: this.minXAxis,
            to: this.maxXAxis,
        };
    }

    public isFrequencyRangeLinkedChanged(): void {
        if (this.variant) return;
        // Trigger update
        this.frequencyRangeChanged(this.frequencyRange);
    }

    public isTimeRangeLinkedChanged(): void {
        // Trigger update
        this.timeRangeChanged(this.timeRange);
    }

    public selectedWeightedDataUnitChanged(): void {
        this.dataUnit = this.selectedWeightedDataUnit;
    }

    public selectedMeasurementIdsChanged(): void {
        // If this is a psychoAcoustic time analysis module, get the psychoAcoustic measurement ids instead
        if (this.variant) {
            this.selectedMeasurementIds = this.selectedPsychoAcousticMeasurementIds;
        }

        this.measurementId =
            this.selectedMeasurementIds && this.selectedMeasurementIds.length > 0
                ? this.selectedMeasurementIds[0]
                : undefined;
        this.dispatchUpdateData();
    }

    public selectedMicChanged(): void {
        this.dispatchUpdateData();
    }

    private measurementUpdated = (property: MeasurementField, measurement: GenericMeasurement): void => {
        // Update measurement fields
        if (this.measurement) {
            MeasurementExtensions.updateMeasurementField(
                this.measurement,
                measurement.id,
                property,
                measurement[property]
            );
        }

        if (property === MeasurementField.mutedSensors) {
            this.moduleInstance.dispatch(() => {
                // Check if we need to invalidate some data
                if (MeasurementExtensions.isRelatedTo(this.measurement, measurement.id)) {
                    this.currentDataId = undefined;
                    return this.updateData();
                }
            });
        }
    };

    public activeRangesChanged(): void {}

    //#endregion

    //#region Spectrum Methods

    private dispatchUpdateData(): void {
        if (this.moduleInstance) this.moduleInstance.dispatch(this.updateData);
    }

    private async updateData(dontRetry?: boolean): Promise<void> {
        try {
            this.progress = 0;
            this.busy = true;
            if (!this.measurementId) {
                this.resetMeasurementData();
                return;
            }

            const selectedMicString = this.variant ? JSON.stringify(this.selectedMic) : "";
            if (this.currentDataId == this.measurementId + selectedMicString) {
                return; // No change
            }

            // Load measurement
            const measurement = await this.getMeasurement();

            this.updateRecommendation(measurement);
            this.updateMeasurementUpdateCallback(measurement);
            // Get spectrum from service and get weighted spectra of
            const spectrumSet: UnitSpectrogram = await this.getSpectrumSet(measurement);
            this.spectra = this.getWeightedSpectra(spectrumSet);

            this.updateBounds(measurement);
            this.progress = 100;

            // Only save measurement if we completed the entire process
            this.measurement = measurement;
            this.currentDataId = this.measurement.id + this.variant ? selectedMicString : "";

            this.moduleInstance.createTimelineEvent();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (e: any) {
            if ((e.statusCode === 0 || e.statusCode === 404) && !dontRetry) {
                // Requeue the analysis
                return this.updateData(true);
            }

            if (e.message) {
                this.moduleInstance
                    .createNotification()
                    .withUrgency(NotificationUrgency.Error)
                    .withMessage(e.message)
                    .show();
            }
        } finally {
            this.busy = false;
        }
    }

    private resetMeasurementData(): void {
        this.spectra = [];
        this.currentDataId = undefined;
        this.measurement = undefined;

        while (this.measurementUpdateDisposables.length > 0) {
            this.measurementUpdateDisposables.pop().dispose();
        }
    }

    private async getMeasurement(): Promise<GenericMeasurement> {
        const measurementPromise =
            this.measurement && this.measurement.id === this.measurementId
                ? Promise.resolve(this.measurement)
                : this.measurementRepository.getMeasurement(this.measurementId);

        // Load measurement
        const measurement = await measurementPromise;
        this.soundProcessingService = await this.processorResolver.resolveProcessor<ISpectralProcessingService>(
            ISpectralProcessingService,
            measurement
        );
        return measurement;
    }

    private updateRecommendation(measurement: GenericMeasurement): void {
        this.recommendedFreqRange = new RecommendationEngine()
            .forMeasurement(measurement)
            .getFrequencyBandRecommendations()
            .map((x) => {
                return <PlotRange>{
                    from: new UnitValue(x.from, Frequency.hertz),
                    to: new UnitValue(x.to, Frequency.hertz),
                };
            });
    }

    private updateMeasurementUpdateCallback(measurement: GenericMeasurement): void {
        // Update the measurement update callback
        while (this.measurementUpdateDisposables.length > 0) {
            this.measurementUpdateDisposables.pop().dispose();
        }

        // Also add an observer to the base measurement if it is a computed measurement
        if (measurement.isComputedMeasurement) {
            this.measurementUpdateDisposables.push(
                this.measurementRepository.measurementUpdateObserver(
                    this.measurementUpdated,
                    measurement.computedMeasurementSourceId
                )
            );
        }

        this.measurementUpdateDisposables.push(
            this.measurementRepository.measurementUpdateObserver(this.measurementUpdated, measurement.id)
        );
    }

    private getSampleRate(measurement: GenericMeasurement): number {
        const sampleRates = measurement.parts
            .filter((x) => x.measurementType === MeasurementPartType.Audio)
            .map((x) => x.device.sampleRate);
        if (sampleRates.length === 0) throw new Error("The selected measurement is corrupted: No data parts available");

        const sampleRate = sampleRates[0];
        for (let i = 1; i < sampleRates.length; i++) {
            if (Math.abs(sampleRate - sampleRates[i]) > Number.EPSILON)
                throw new Error("Multirate measurements are not supported");
        }
        return sampleRate;
    }

    private getStepSize(duration: number, windowSize: number, sampleRate: number): number {
        let stepSize: number;
        // If the duration is small, take a smaller step size for a clearer image
        if (duration <= 6) stepSize = windowSize / 5;
        else if (duration <= 10) stepSize = windowSize / 3;
        // Duration is very long, do not take smaller steps to prevent performance issues
        else stepSize = windowSize;
        return stepSize;
    }

    private getOffsets(duration: number, stepSize: number, windowSize: number, sampleRate: number): number[] {
        // Make sure we have the entire measurement duration covered (the computation allows for extrapolation around the edges)
        const offsets: number[] = [];
        for (let i = 0; i <= duration * sampleRate + Math.min(stepSize, Math.ceil(windowSize / 2)); i += stepSize) {
            offsets.push((i - Math.ceil(windowSize / 2)) / sampleRate);
        }
        return offsets;
    }

    private async getSpectrumSet(measurement: GenericMeasurement): Promise<UnitSpectrogram> {
        const duration = measurement.duration;
        const sampleRate = this.getSampleRate(measurement);
        const windowSize = sampleRate / 25;
        const stepSize: number = this.getStepSize(duration, windowSize, sampleRate);

        const offsets = this.getOffsets(duration, stepSize, windowSize, sampleRate);

        const properties: SpectrumSetAnalysisProperties = {
            duration: windowSize / sampleRate,
            timeOffsets: offsets,
            useIV64Method: false,
            requestedMicrophones: this.variant ? [this.selectedMic] : undefined,
        };
        // Get spectrum from service
        let result = await this.spectrogramProvider.getSpectrumSet(measurement, properties, (progressUpdate) => {
            this.progress = Math.round(progressUpdate * 100);
        });

        // This copy is needed to make sure that caching works, otherwise closing and opening the spectrogram module will not show the spectrogram
        return ObjectExtensions.copy(result);
    }

    private getWeightedSpectra(unitspectrogram: UnitSpectrogram): WeightedSpectrum[] {
        return unitspectrogram.spectrumSet.map((s) => {
            // Reuse the already allocated array of values for holding the spectrum points
            const spectrumData = <Point2D[]>(<unknown[]>s.data);

            for (let j = 0; j < spectrumData.length; j++) {
                spectrumData[j] = { x: unitspectrogram.yAxisValues[j], y: s.data[j] };
            }

            const spectrum = UnitSpectrum.FromSpectrum(unitspectrogram.yAxisUnit, unitspectrogram.dataUnit, <Spectrum>{
                data: spectrumData,
                properties: {
                    timeRange: s.properties.timeRange,
                },
            });

            if (this.variant) {
                return WeightedSpectrum.FromUnitSpectrum(new NoneWeighting(), spectrum);
            } else {
                return WeightedSpectrum.FromUnitSpectrum(new ZWeighting(), spectrum);
            }
        });
    }

    private updateBounds(measurement: GenericMeasurement): void {
        this.minXAxisBounds = 0;
        this.maxXAxisBounds = measurement.duration;

        this.minYAxisBounds = this.mininumDisplayedYAxisValue.value;

        const sampleRate = this.getSampleRate(measurement);
        if (sampleRate) {
            this.maxYAxisBounds = sampleRate / 2;
        } else {
            this.maxYAxisBounds = this.maximumDisplayedYAxisValue.value;
        }

        if (!this.minYAxis || this.minYAxis < this.minYAxisBounds) this.minYAxis = this.minYAxisBounds;

        if (!this.maxYAxis || this.maxYAxis > this.maxYAxisBounds) this.maxYAxis = this.maxYAxisBounds;

        if (!this.minXAxis || this.minXAxis < this.minXAxisBounds) this.minXAxis = this.minXAxisBounds;

        if (!this.maxXAxis || this.maxXAxis > this.maxXAxisBounds) this.maxXAxis = this.maxXAxisBounds;

        this.frequencyRangeInputChanged();
        this.timeRangeInputChanged();
    }
    //#endregion

    //#region Export Functions

    public exportToSvg(): Promise<SVGNode> {
        // Get svg from spectrogram
        return Promise.all([this.dataExportable.exportToSvg(), this.colorbarExportable.exportToSvg()]).then((svgs) => {
            const spectrogramSvg = svgs[0];
            const colorbarSvg = svgs[1];

            // Get size for both svgs
            const spectrogramSize = this.getSvgBounds(spectrogramSvg);
            const colorBarSize = this.getSvgBounds(colorbarSvg);

            // Now we manually need to merge the two svgs
            const newX = 0;
            const newY = 0;
            const newWidth = Math.max(spectrogramSize.width, colorBarSize.width);
            const newHeight = spectrogramSize.height + colorBarSize.height;

            // Resize the spectrogramsvg to be large enough for both
            spectrogramSvg.setAttribute(
                "viewBox",
                newX.toFixed(2) + "," + newY.toFixed(2) + "," + newWidth.toFixed(2) + "," + newHeight.toFixed(2)
            );

            // Add two g elements to hold both sub-svgs
            let gNode = new SVGNode("g");
            gNode.setAttribute(
                "transform",
                "translate(" +
                    ((newWidth - spectrogramSize.width) / 2 - spectrogramSize.x).toFixed(2) +
                    "," +
                    (-spectrogramSize.y).toFixed(2) +
                    ")"
            );
            for (const child of spectrogramSvg.children) {
                gNode.addChild(child);
            }
            spectrogramSvg.clearChildren();
            spectrogramSvg.addChild(gNode);

            // Add a g element to hold the colorbar
            gNode = new SVGNode("g");
            gNode.setAttribute(
                "transform",
                "translate(" +
                    ((newWidth - colorBarSize.width) / 2 - colorBarSize.x).toFixed(2) +
                    "," +
                    spectrogramSize.height.toFixed(2) +
                    ")"
            );
            for (const child of colorbarSvg.children) {
                gNode.addChild(child);
            }

            spectrogramSvg.addChild(gNode);
            return spectrogramSvg;
        });
    }

    public exportVisual(): ImageExportState {
        const exportModalState: ImageExportState = {
            measurementIds: [this.measurementId],
            exportable: this,
            isLegendDisplayed: false,
            initialFileName: this.measurement ? this.measurement.name + "_Spectrogram" : undefined,
            initialLogoPosition: LogoPositions.topRight,
            namedProperties: [
                { name: "[Weighting]", value: this.selectedWeightedDataUnit.weighting.name + " Weighting" },
                {
                    name: "[Frequencies]",
                    value: FileNameConversion.convertSelectedFrequencies(this.frequencySelectionService.ranges),
                },
            ],
        };

        return exportModalState;
    }

    private getSvgBounds(svg: SVGNode): { x: number; y: number; width: number; height: number } {
        const attrib = svg.attributes["viewBox"];

        if (!attrib) return undefined;

        const splits = attrib.split(",");
        if (splits.length != 4) return undefined;

        return {
            x: Number(splits[0]),
            y: Number(splits[1]),
            width: Number(splits[2]),
            height: Number(splits[3]),
        };
    }

    //#endregion

    //#region UI Functions

    public linkFrequencyRange(link: boolean): void {
        this.isFrequencyRangeLinked = link;
    }

    public linkTimeRange(link: boolean): void {
        this.isTimeRangeLinked = link;
    }

    public timeSelectionRangesChanged(): void {
        if (this.timeRangeModal && this.timeRangeModal.isOpen) {
            this.timeRangeModal.updateState({
                selectedTimeRange: this.timeSelectionRanges.length > 0 ? this.timeSelectionRanges[0] : undefined,
                currentSignals: [
                    {
                        measurement: this.measurement,
                        selectedChannels: undefined,
                        timeDatas: undefined,
                    } as TimeDataSetWithAudio,
                ],
            });
        }
    }

    public openTimeRangeModal(): void {
        // for spectrogram, ignore selectedChannels and timeDatas.
        this.moduleInstance
            .createModal<TimeSelectionModalState, void>(WorkflowModalCatalog.TimeRangeModal)
            .withState({
                selectedTimeRange: this.timeSelectionRanges.length > 0 ? this.timeSelectionRanges[0] : undefined,
                currentSignals: [
                    {
                        measurement: this.measurement,
                        selectedChannels: undefined,
                        timeDatas: undefined,
                    } as TimeDataSetWithAudio,
                ],
            })
            .whenClosed(() => {
                this.timeRangeModal = undefined;
            })
            .show()
            .then((x) => {
                this.timeRangeModal = x;
            });
    }

    public openSpectrogramSettingsModal(): void {
        if (this.spectrogramSettingsModal && this.spectrogramSettingsModal.isOpen) {
            this.spectrogramSettingsModal.cancel();
            this.spectrogramSettingsModal = undefined;
            return;
        }
        this.moduleInstance
            .createModal<SettingsModalState<SpectrogramModule>, void>(WorkflowModalCatalog.SpectrogramSettingsModal)
            .withState({ belongsTo: this })
            .whenClosed(() => {
                this.spectrogramSettingsModal = undefined;
            })
            .show()
            .then((x) => {
                this.spectrogramSettingsModal = x;
            });
    }
    //#endregion

    //#region IExpandableWorkflowModule Members
    public canExpand = true;
    //#endregion
}
